import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UpgradeTier } from '../interceptors/pricing-error.interceptor';
import { TierUpgradeDialogComponent } from '../components/tier-upgrade-dialog/tier-upgrade-dialog.component';

/**
 * Context for showing the tier limit upgrade dialog.
 * Passed from the interceptor when a TIER_LIMIT_EXCEEDED error is received.
 */
export interface TierLimitContext {
  metric: string;           // e.g. 'integrations', 'sequences', 'teamMembers'
  currentUsage: number;     // current usage count
  limit: number;            // current tier limit
  upgradeTiers: UpgradeTier[];  // tiers that would accommodate the limit
  message?: string;         // optional backend message
}

/**
 * Maps backend metric names to user-friendly display names
 */
const METRIC_DISPLAY_NAMES: Record<string, string> = {
  integrations: 'Integrations',
  sequences: 'Sequences',
  teamMembers: 'Team Members',
  linkedinProspects: 'LinkedIn Prospects',
  emailProspects: 'Email Prospects',
  aiCredits: 'AI Credits',
  patterns: 'Patterns',
  resource: 'Resources'
};

/**
 * Service to show the tier upgrade dialog when a user hits a tier limit.
 * Can be called from the pricing error interceptor or directly from any component.
 *
 * Usage:
 *   tierUpgradeService.showUpgradeDialog({
 *     metric: 'integrations',
 *     currentUsage: 1,
 *     limit: 1,
 *     upgradeTiers: [{ tierId: 'pro', name: 'Pro', limit: 10, price: 100 }]
 *   });
 */
@Injectable({ providedIn: 'root' })
export class TierUpgradeService {
  private activeDialogRef: DynamicDialogRef | null = null;

  constructor(
    private dialogService: DialogService,
    private router: Router
  ) {}

  /**
   * Get the human-readable display name for a metric
   */
  getMetricDisplayName(metric: string): string {
    return METRIC_DISPLAY_NAMES[metric] || metric;
  }

  /**
   * Show the tier upgrade dialog with tier options.
   * If a dialog is already open, closes it first to avoid stacking.
   */
  showUpgradeDialog(context: TierLimitContext): DynamicDialogRef {
    // Close any existing upgrade dialog
    if (this.activeDialogRef) {
      this.activeDialogRef.close();
      this.activeDialogRef = null;
    }

    const metricDisplayName = this.getMetricDisplayName(context.metric);

    this.activeDialogRef = this.dialogService.open(TierUpgradeDialogComponent, {
      header: `${metricDisplayName} Limit Reached`,
      width: '560px',
      modal: true,
      dismissableMask: true,
      styleClass: 'tier-upgrade-dialog',
      data: {
        metric: context.metric,
        metricDisplayName,
        currentUsage: context.currentUsage,
        limit: context.limit,
        upgradeTiers: context.upgradeTiers,
        message: context.message
      }
    });

    this.activeDialogRef.onClose.subscribe((result: any) => {
      this.activeDialogRef = null;

      // If the user selected a tier, navigate to billing
      if (result?.selectedTierId) {
        this.navigateToUpgrade(result.selectedTierId);
      }
    });

    return this.activeDialogRef;
  }

  /**
   * Navigate to the billing page with upgrade context.
   * The billing component reads sessionStorage.upgradeContext to pre-select the tier.
   */
  navigateToUpgrade(tierId?: string): void {
    // Update the upgrade context with the selected tier
    const existingContext = sessionStorage.getItem('upgradeContext');
    if (existingContext) {
      try {
        const context = JSON.parse(existingContext);
        context.selectedTierId = tierId;
        sessionStorage.setItem('upgradeContext', JSON.stringify(context));
      } catch {
        // If parse fails, create new context
        sessionStorage.setItem('upgradeContext', JSON.stringify({
          reason: 'limit_exceeded',
          selectedTierId: tierId,
          timestamp: Date.now()
        }));
      }
    } else {
      sessionStorage.setItem('upgradeContext', JSON.stringify({
        reason: 'limit_exceeded',
        selectedTierId: tierId,
        timestamp: Date.now()
      }));
    }

    this.router.navigate(['/billing']);
  }
}
